/* eslint eslint-comments/no-use: off */
/* eslint-disable relay/unused-fields */
import {Box, Button, Octicon, Text} from '@primer/react'
import {type ExtendedItemProps, ItemPicker} from './ItemPicker'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {ERRORS, LABELS} from '../constants'
import {GitBranchIcon, GitPullRequestIcon, TriangleDownIcon} from '@primer/octicons-react'
import {
  type PreloadedQuery,
  readInlineData,
  usePreloadedQuery,
  useRelayEnvironment,
  fetchQuery,
  useFragment,
  graphql,
} from 'react-relay'
import type {
  PullRequestPickerSearchQuery,
  PullRequestPickerSearchQuery$data,
} from './__generated__/PullRequestPickerSearchQuery.graphql'
import type {
  PullRequestPickerQuery$data,
  PullRequestPickerQuery$key,
} from './__generated__/PullRequestPickerQuery.graphql'
import type {
  PullRequestPickerPullRequest$data,
  PullRequestPickerPullRequest$key,
} from './__generated__/PullRequestPickerPullRequest.graphql'
import {debounce} from '@github/mini-throttle'
import {useToastContext} from '@github-ui/toast'

import type {ItemGroup} from '../shared'
import {
  PullRequestPickerFragment,
  PullRequestPickerQueryFragment,
  PullRequestPickerSearchPullRequestsGraphQLQuery,
  getPrIcon,
  getPrIconColor,
  getPullRequestSearchQueries,
} from './PullRequestPicker'
import type {
  BranchPickerSearchBranchesQuery,
  BranchPickerSearchBranchesQuery$data,
} from './__generated__/BranchPickerSearchBranchesQuery.graphql'
import {BranchPickerRefFragment, BranchPickerSearchBranches, BranchPickerSearchBranchesFragment} from './BranchPicker'
import type {BranchPickerRef$data, BranchPickerRef$key} from '../branch'
import type {BranchPickerSearchBranchesFragment$key} from './__generated__/BranchPickerSearchBranchesFragment.graphql'

type PullRequestAndBranchPickerSharedProps = {
  onSelectionChange: (selected: Array<PullRequestPickerPullRequest$data | BranchPickerRef$data>) => void
  anchorElement?: (props: React.HTMLAttributes<HTMLElement>) => JSX.Element
  subtitle?: string | React.ReactElement
  title?: string | React.ReactElement
  triggerOpen?: boolean
  preventClose?: boolean
}

export type PullRequestAndBranchPickerProps = PullRequestAndBranchPickerSharedProps & {
  repoNameWithOwner: string
  pullRequestQueryRef: PreloadedQuery<PullRequestPickerSearchQuery>
  initialSelectedPrs: PullRequestPickerPullRequest$data[]
  branchesQueryRef: PreloadedQuery<BranchPickerSearchBranchesQuery>
  initialSelectedBranches: BranchPickerRef$data[]
  preventClose?: boolean
}

type PullRequestPickerBaseProps = PullRequestAndBranchPickerSharedProps & {
  pullRequestItems: PullRequestPickerPullRequest$data[]
  branchItems: BranchPickerRef$data[]
  initialSelectedItems?: Array<PullRequestPickerPullRequest$data | BranchPickerRef$data>
  onFilter: (value: string) => void
  loading?: boolean
}

type PullRequestAndBranchPickerInternalProps = Omit<
  PullRequestAndBranchPickerProps,
  'pullRequestQueryRef' | 'branchesQueryRef'
> & {
  pullRequestsfragmentKey: PullRequestPickerQuery$key
  branchesFragmentKey: BranchPickerSearchBranchesQuery$data
}

export const PullRequestAndBranchPickerBranchFragment = graphql`
  fragment PullRequestAndBranchPickerBranch on PullRequest @inline {
    id
    url
    number
    title
    state
    isDraft
    repository {
      id
      name
      nameWithOwner
      owner {
        login
        __typename
      }
    }
  }
`

export const PullRequestAndBranchPickerPlaceholder = () => (
  <Button
    leadingVisual={GitPullRequestIcon}
    trailingVisual={TriangleDownIcon}
    aria-labelledby="pr-picker-label"
    disabled
  >
    {LABELS.selectPr}
  </Button>
)

export const PullRequestAndBranchPicker = ({
  pullRequestQueryRef,
  branchesQueryRef,
  ...rest
}: PullRequestAndBranchPickerProps) => {
  const pullRequestsData = usePreloadedQuery(PullRequestPickerSearchPullRequestsGraphQLQuery, pullRequestQueryRef)
  const branchesData = usePreloadedQuery(BranchPickerSearchBranches, branchesQueryRef)

  return pullRequestsData && branchesData ? (
    <PullRequestAndBranchPickerInternal
      pullRequestsfragmentKey={pullRequestsData}
      branchesFragmentKey={branchesData}
      {...rest}
    />
  ) : (
    <></>
  )
}

const selectedGroup: ItemGroup = {groupId: 'selected'}
const suggestionsGroup: ItemGroup = {groupId: 'suggestions', header: {title: 'Suggestions', variant: 'filled'}}

const PullRequestAndBranchPickerInternal = ({
  repoNameWithOwner,
  initialSelectedPrs,
  pullRequestsfragmentKey,
  branchesFragmentKey,
  initialSelectedBranches,
  ...rest
}: PullRequestAndBranchPickerInternalProps) => {
  const {addToast} = useToastContext()
  const relayEnvironment = useRelayEnvironment()
  const [pullRequestsQueryFragmentKey, setPullRequestsQueryFragmentKey] =
    useState<PullRequestPickerQuery$key>(pullRequestsfragmentKey)
  const [filter, setFilter] = useState('')
  const [searchLoading, setSearchLoading] = useState<boolean>(false)

  const fetchedPrData = useFragment<PullRequestPickerQuery$key>(
    PullRequestPickerQueryFragment,
    pullRequestsQueryFragmentKey,
  )
  const fetchedLinkedPullRequests = useMemo<PullRequestPickerPullRequest$data[]>(() => {
    const map = new Map<string, PullRequestPickerPullRequest$data>()
    const searchedPRs = getLinkedPullRequests(fetchedPrData)
    for (const spr of searchedPRs) {
      map.set(spr.id, spr)
    }
    for (const ipr of initialSelectedPrs) {
      map.set(ipr.id, ipr)
    }
    return [...map.values()].sort(sortPrsByCreationDate)
  }, [fetchedPrData, initialSelectedPrs])

  const [branchesQueryFragmentData, setBranchesQueryFragmentData] =
    useState<BranchPickerSearchBranchesQuery$data>(branchesFragmentKey)

  const searchedBranchesData = useFragment<BranchPickerSearchBranchesFragment$key>(
    BranchPickerSearchBranchesFragment,
    branchesQueryFragmentData,
  )

  const fetchedBranches = useMemo<BranchPickerRef$data[]>(() => {
    const map = new Map<string, BranchPickerRef$data>()

    const searchedBranches =
      searchedBranchesData.repository?.refs?.edges?.flatMap(edge =>
        // eslint-disable-next-line no-restricted-syntax
        edge?.node ? [readInlineData<BranchPickerRef$key>(BranchPickerRefFragment, edge.node)] : [],
      ) ?? []
    for (const searchBranch of searchedBranches) {
      map.set(searchBranch.id, searchBranch)
    }
    for (const initialBranch of initialSelectedBranches) {
      map.set(initialBranch.id, initialBranch)
    }

    return [...map.values()]
  }, [initialSelectedBranches, searchedBranchesData.repository?.refs?.edges])

  const fetchhBranchesSearchData = useCallback(
    (query: string) => {
      const repoNameWithOwnerParts = repoNameWithOwner.split('/')
      const owner = repoNameWithOwnerParts[0]
      const repo = repoNameWithOwnerParts[1]
      if (!owner || !repo) {
        return
      }
      return new Promise<BranchPickerRef$data[]>((resolve, reject) => {
        fetchQuery<BranchPickerSearchBranchesQuery>(relayEnvironment, BranchPickerSearchBranches, {
          owner,
          name: repo,
          query,
          first: 25,
        }).subscribe({
          next: (data: BranchPickerSearchBranchesQuery$data) => {
            if (data !== null) {
              // const suggestedBranches = (data.repository?.refs?.edges || []).flatMap(edge =>
              //   edge?.node ? [readInlineData<BranchPickerRef$key>(BranchPickerRefFragment, edge.node)] : [],
              // )
              // resolve(suggestedBranches)
              setBranchesQueryFragmentData(data)
              setSearchLoading(false)
            }
          },
          error: (error: Error) => {
            reject(error)
          },
        })
      })
    },
    [relayEnvironment, repoNameWithOwner],
  )

  const fetchPullRequestsSearchData = useCallback(
    (searchQuery: string) => {
      // run query on search
      setSearchLoading(true)
      const queryString = `${searchQuery} sort:created-desc`
      fetchQuery<PullRequestPickerSearchQuery>(
        relayEnvironment,
        PullRequestPickerSearchPullRequestsGraphQLQuery,
        getPullRequestSearchQueries(repoNameWithOwner, queryString),
      ).subscribe({
        next: (data: PullRequestPickerSearchQuery$data) => {
          if (data !== null) {
            setPullRequestsQueryFragmentKey(data)
            setSearchLoading(false)
          }
        },
        error: () => {
          setSearchLoading(false)
          addToast({
            type: 'error',
            message: ERRORS.couldNotSearchPullRequests,
          })
        },
      })
    },
    [addToast, relayEnvironment, repoNameWithOwner],
  )

  const fetchSearchData = useCallback(
    (query: string) => {
      fetchhBranchesSearchData(query)
      fetchPullRequestsSearchData(query)
    },
    [fetchPullRequestsSearchData, fetchhBranchesSearchData],
  )

  function onFilter(value: string) {
    setFilter(value)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFetchSearchData = useCallback(
    debounce((nextValue: string) => fetchSearchData(nextValue), 200),
    [fetchSearchData],
  )

  useEffect(() => {
    if (filter.length === 0) {
      setPullRequestsQueryFragmentKey(pullRequestsfragmentKey)
    }
    debounceFetchSearchData(filter)
  }, [debounceFetchSearchData, filter, pullRequestsfragmentKey])

  return (
    <PullRequestAndBranchPickerBase
      pullRequestItems={fetchedLinkedPullRequests}
      branchItems={fetchedBranches}
      initialSelectedItems={[...initialSelectedPrs, ...initialSelectedBranches]}
      onFilter={onFilter}
      loading={searchLoading}
      {...rest}
    />
  )
}

export function PullRequestAndBranchPickerBase({
  pullRequestItems,
  branchItems,
  initialSelectedItems = [],
  onFilter,
  anchorElement,
  ...rest
}: PullRequestPickerBaseProps) {
  const pullRequestPickerRef = useRef<HTMLButtonElement>(null)

  // Not showing the default branch or branches that already have a PR
  const filteredBranchItems = useMemo(() => {
    return branchItems.filter(
      branch => branch.id !== branch.repository.defaultBranchRef?.id && branch.associatedPullRequests.totalCount === 0,
    )
  }, [branchItems])

  const filterItems = useCallback(
    (value: string) => {
      onFilter(value)
    },
    [onFilter],
  )

  const getItemKey = useCallback((item: PullRequestPickerPullRequest$data | BranchPickerRef$data) => item.id, [])

  const groups: ItemGroup[] = useMemo(() => {
    const itemGroups = []
    if (initialSelectedItems.length > 0) {
      itemGroups.push(selectedGroup)
    }
    if (
      branchItems.length + pullRequestItems.length > 0 &&
      pullRequestItems.length + branchItems.length - initialSelectedItems.length > 0
    ) {
      itemGroups.push(suggestionsGroup)
    }
    return itemGroups
  }, [branchItems.length, initialSelectedItems.length, pullRequestItems.length])

  const convertToItemProps = useCallback(
    (
      item: PullRequestPickerPullRequest$data | BranchPickerRef$data,
    ): ExtendedItemProps<PullRequestPickerPullRequest$data | BranchPickerRef$data> => {
      const isPr = item.id.startsWith('PR_')
      const displayName = isPr ? (item as PullRequestPickerPullRequest$data).title : (item as BranchPickerRef$data).name
      const displaySubtitle = isPr
        ? `${(item as PullRequestPickerPullRequest$data).repository.nameWithOwner}#${
            (item as PullRequestPickerPullRequest$data).number
          }`
        : 'no pull request'
      const icon = isPr ? (
        <Octicon
          icon={getPrIcon(item as PullRequestPickerPullRequest$data)}
          size={16}
          sx={{path: {fill: getPrIconColor(item as PullRequestPickerPullRequest$data)}}}
        />
      ) : (
        <Octicon icon={GitBranchIcon} size={16} />
      )

      return {
        // this is a hack to make sure that we are using the prop
        id: `${item.id}`,
        groupId: initialSelectedItems.find(r => r.id === item.id) ? selectedGroup.groupId : suggestionsGroup.groupId,
        children: (
          <Box sx={{display: 'flex', flexDirection: 'column'}}>
            <Text>{displayName}</Text>
            <Text sx={{fontSize: 0, color: 'fg.muted'}}>{displaySubtitle}</Text>
          </Box>
        ),
        source: item,
        leadingVisual: () => icon,
      }
    },
    [initialSelectedItems],
  )

  const renderAnchor = useCallback(
    ({...anchorProps}: React.HTMLAttributes<HTMLElement>) => {
      if (anchorElement) {
        return anchorElement(anchorProps)
      }

      return (
        <Button
          leadingVisual={GitPullRequestIcon}
          trailingVisual={TriangleDownIcon}
          {...anchorProps}
          aria-labelledby="pr-picker-label"
          ref={pullRequestPickerRef}
        >
          {initialSelectedItems.length > 0
            ? LABELS.getNumberOfSelectedPrsLabel(initialSelectedItems.length)
            : LABELS.selectPr}
        </Button>
      )
    },
    [anchorElement, initialSelectedItems.length],
  )

  return (
    <Box sx={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1}}>
      <ItemPicker
        items={[...pullRequestItems, ...filteredBranchItems]}
        initialSelectedItems={initialSelectedItems}
        groups={groups}
        filterItems={filterItems}
        getItemKey={getItemKey}
        convertToItemProps={convertToItemProps}
        placeholderText={LABELS.searchPr}
        selectionVariant="multiple"
        selectPanelRef={pullRequestPickerRef}
        renderAnchor={renderAnchor}
        width="medium"
        resultListAriaLabel={'Pull request results'}
        height="large"
        {...rest}
      />
    </Box>
  )
}

function getLinkedPullRequests(data: PullRequestPickerQuery$data): PullRequestPickerPullRequest$data[] {
  const {commenters, mentions, assignee, author, open} = data

  const allPrs = [
    ...(commenters?.nodes || []),
    ...(mentions?.nodes || []),
    ...(assignee?.nodes || []),
    ...(author?.nodes || []),
    ...(open?.nodes || []),
  ]

  const fetchedPrsMap = new Map<string, PullRequestPickerPullRequest$data>()

  for (const pr of allPrs) {
    // eslint-disable-next-line no-restricted-syntax
    const prData = readInlineData<PullRequestPickerPullRequest$key>(PullRequestPickerFragment, pr)
    if (prData) {
      fetchedPrsMap.set(prData.id, prData)
    }
  }

  return Array.from(fetchedPrsMap.values())
}

function sortPrsByCreationDate(first: PullRequestPickerPullRequest$data, second: PullRequestPickerPullRequest$data) {
  const firstDate = new Date(first.createdAt)
  const secondDate = new Date(second.createdAt)

  if (firstDate < secondDate) {
    return 1
  } else if (firstDate > secondDate) {
    return -1
  } else {
    return 0
  }
}

try{ PullRequestAndBranchPickerPlaceholder.displayName ||= 'PullRequestAndBranchPickerPlaceholder' } catch {}
try{ PullRequestAndBranchPicker.displayName ||= 'PullRequestAndBranchPicker' } catch {}
try{ PullRequestAndBranchPickerInternal.displayName ||= 'PullRequestAndBranchPickerInternal' } catch {}
try{ PullRequestAndBranchPickerBase.displayName ||= 'PullRequestAndBranchPickerBase' } catch {}